import { Link, useParams } from "react-router-dom";
import { BLOG_SLUG_TO_POST } from "../data/blogPosts";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import BlogCTA from "../components/BlogCTA";
import BlogAuthor from "../components/BlogAuthor";
import SixTraitChart from "../components/SixTraitChart";

const SITE_URL = "https://thephilosophertest.com";
const DEFAULT_OG = `${SITE_URL}/og-default.png`;
const AUTHOR_URL = `${SITE_URL}/blog`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
function formatDate(iso) {
  const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

// A paragraph that starts with a straight double-quote is a blockquote.
function isQuoteParagraph(p) {
  return typeof p === "string" && p.trim().startsWith("\"") && p.trim().endsWith("\"");
}

function stripQuotes(p) {
  return p.trim().replace(/^"|"$/g, "");
}

/**
 * /blog/:slug — long-form article page. Layout and typography match the
 * philosopher pages (report-body class, marble & gold palette) so the two
 * content hubs feel like the same site.
 */
export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_SLUG_TO_POST[slug];

  if (!post) {
    return (
      <div className="relative z-10 mx-auto min-h-[100dvh] max-w-md px-6 py-20 text-center">
        <SEOHead noindex path={`/blog/${slug || ""}`} />
        <h1 className="font-serif text-3xl" style={{ color: "var(--gold)" }}>
          Not found
        </h1>
        <p className="mt-4" style={{ color: "var(--muted)" }}>
          <Link to="/blog" style={{ color: "var(--gold)" }}>
            Back to the blog
          </Link>
        </p>
      </div>
    );
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const ogImage = (post.coverImage && post.coverImage.src) || DEFAULT_OG;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.datePublished,
    dateModified: post.datePublished,
    author: {
      "@type": "Person",
      name: post.author || "Jonathan Mendola",
      url: AUTHOR_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "The Philosopher Test",
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_OG,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    image: ogImage,
    url,
  };

  // Locate the last <h3> section so we can inject the mid-article CTA
  // immediately after it — matches the "après la dernière section H3" spec
  // and works for any future article without configuration.
  const sections = post.sections || [];
  let lastH3Index = -1;
  for (let i = 0; i < sections.length; i++) {
    if (sections[i].h3) lastH3Index = i;
  }

  return (
    <div
      className="relative z-10 mx-auto min-h-[100dvh] w-full px-5 py-14"
      data-testid="blog-post-screen"
    >
      <SEOHead
        title={post.title}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        jsonLd={jsonLd}
        ogImage={ogImage}
      />
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <p
          className="text-[11px] font-medium uppercase"
          style={{ color: "var(--muted)", letterSpacing: "0.32em" }}
        >
          Blog · The Philosopher Test
        </p>
        <h1
          className="mt-6 font-serif text-[36px] sm:text-[46px] leading-[1.08]"
          style={{ color: "var(--gold)" }}
          data-testid="blog-post-h1"
        >
          {post.h1 || post.title}
        </h1>
        <p
          className="mt-3 text-[13px]"
          style={{ color: "var(--muted)" }}
          data-testid="blog-post-date"
        >
          <time dateTime={post.datePublished}>{formatDate(post.datePublished)}</time>
          {" · "}by {post.author || "Jonathan Mendola"}
        </p>

        {post.coverImage && post.coverImage.src && (
          <figure className="blog-cover" data-testid="blog-cover">
            <img
              src={post.coverImage.src}
              alt={post.coverImage.alt}
              loading="eager"
              decoding="async"
              className="blog-cover-img"
              data-testid="blog-cover-img"
            />
            {post.coverImage.credit && (
              <figcaption
                className="blog-figcaption"
                data-testid="blog-cover-credit"
              >
                Portrait: {post.coverImage.credit.author},{" "}
                <a
                  href={post.coverImage.credit.source}
                  target="_blank"
                  rel="noopener nofollow"
                >
                  via Wikimedia Commons
                </a>
                ,{" "}
                {post.coverImage.credit.licenseUrl ? (
                  <a
                    href={post.coverImage.credit.licenseUrl}
                    target="_blank"
                    rel="noopener nofollow license"
                  >
                    {post.coverImage.credit.license}
                  </a>
                ) : (
                  post.coverImage.credit.license
                )}
                .
              </figcaption>
            )}
          </figure>
        )}

        <div className="report-body mt-8" data-testid="blog-post-body">
          {(post.intro || []).map((p, i) => (
            <p key={`intro-${i}`}>{p}</p>
          ))}

          {sections.map((sec, i) => (
            <div key={`sec-wrap-${i}`}>
              <BlogSection sec={sec} slug={post.slug} />
              {i === lastH3Index && (
                <BlogCTA testid="blog-cta-mid" />
              )}
            </div>
          ))}

          <BlogCTA testid="blog-cta-end" />
          <BlogAuthor testid="blog-author" />
        </div>

        <div className="mt-10 flex flex-col gap-3">
          <Link
            to="/"
            className="btn-primary text-center"
            data-testid="blog-cta-take-test"
          >
            Take The Philosopher Test
          </Link>
          <Link
            to="/blog"
            className="btn-ghost text-center"
            data-testid="blog-back-link"
          >
            Back to the blog
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function BlogSection({ sec, slug }) {
  const paragraphs = sec.paragraphs || [];
  const hasImage = sec.image && sec.image.src;
  return (
    <>
      {sec.h2 && <h2>{sec.h2}</h2>}
      {sec.h3 && <h3>{sec.h3}</h3>}
      {hasImage && (
        <figure className="blog-figure" data-testid="blog-section-figure">
          <img
            src={sec.image.src}
            alt={sec.image.alt}
            loading="lazy"
            decoding="async"
            className="blog-figure-img"
          />
          {sec.image.caption && (
            <figcaption className="blog-figcaption">
              {sec.image.caption}
            </figcaption>
          )}
        </figure>
      )}
      {sec.chart && (
        <SixTraitChart
          scores={sec.chart.scores}
          title={sec.chart.title}
          testid={sec.chart.testid || "six-trait-chart"}
        />
      )}
      {paragraphs.map((p, i) =>
        isQuoteParagraph(p) ? (
          <blockquote key={i}>{stripQuotes(p)}</blockquote>
        ) : (
          <p key={i}>{p}</p>
        ),
      )}
      {sec.list && (
        <ul>
          {sec.list.map((li, i) => (
            <li key={i}>{li}</li>
          ))}
        </ul>
      )}
      {(sec.afterList || []).map((p, i) => (
        <p key={`after-${i}`}>{p}</p>
      ))}
      {sec.crossLink && (
        <p data-testid="blog-cross-link-paragraph">
          {sec.crossLink.before}
          <Link
            to={sec.crossLink.to}
            style={{ color: "var(--gold)" }}
            data-testid="blog-cross-link"
          >
            {sec.crossLink.linkText}
          </Link>
          {sec.crossLink.after}
        </p>
      )}
      {sec.closingLinks && <ClosingLinksParagraph slug={slug} />}
    </>
  );
}

// Slug-dispatched closing block. Each article has its own final paragraphs
// with inline internal links, kept as real <Link> anchors (not stringified).
function ClosingLinksParagraph({ slug }) {
  if (slug === "nietzsche-personality-type") return <NietzscheClosing />;
  return <MarcusClosing />;
}

function MarcusClosing() {
  return (
    <p>
      That's what <Link to="/" style={{ color: "var(--gold)" }}>The Philosopher Test</Link>{" "}
      measures: twenty questions, six traits, and a match with one of eight great
      minds — Marcus Aurelius among them. It takes about three minutes and gives
      you your full chart, free, with no signup.
      {" "}
      If you want the longer profile first — his five defining signs, his best
      quotes and where to start reading him — the{" "}
      <Link to="/philosophers/marcus-aurelius" style={{ color: "var(--gold)" }}>
        Marcus Aurelius personality profile
      </Link>{" "}
      covers all of it. And if you'd rather see how he compares with the other
      seven, the{" "}
      <Link to="/philosophers" style={{ color: "var(--gold)" }}>
        full list of philosopher profiles
      </Link>{" "}
      is here.
    </p>
  );
}

function NietzscheClosing() {
  return (
    <>
      <p>
        <Link to="/" style={{ color: "var(--gold)" }}>The Philosopher Test</Link>{" "}
        measures those six traits across twenty questions and matches you with
        one of eight great minds. Three minutes, free, no signup, and you get
        your full chart, not a label.
      </p>
      <p>
        If you want the deeper profile first, the{" "}
        <Link to="/philosophers/nietzsche" style={{ color: "var(--gold)" }}>
          Nietzsche personality profile
        </Link>{" "}
        covers his defining signs, his best lines and where to start reading him
        without getting lost. The{" "}
        <Link to="/philosophers" style={{ color: "var(--gold)" }}>
          other seven profiles are here
        </Link>
        .
      </p>
    </>
  );
}
